import {inject, Injectable} from '@angular/core';
import {ClickFinderStore} from '../../stores/click-finder.store';
import {VaultsService as ApiVaultService} from '../../api/services/vaults.service';
import {DocumentTypeCategoriesService as ApiDocumentTypeCategoriesService} from '../../api/services/document-type-categories.service';
import {DocumentTypesService as ApiDocumentTypesService} from '../../api/services/document-types.service';
import {ClickFinderNodeWithIcon} from '../../models/click-finder-node-with-icon.model';
import {TagQuery} from '../../queries/tag.query';
import {map} from 'rxjs/operators';
import {SnackbarService} from '../snackbar/snackbar.service';
import {ClickFinderNode} from 'src/app/api/models/click-finder-node';
import {TranslationKey} from '../../types/available-translations.type';
import {DocumentType} from '../../api/models/document-type';
import {Icon} from '../../types/icons/icon.type';
import {ICONS} from 'src/app/constants/icons/icons.constants';
import {HttpContext, HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {defaultTagGroupDefinitions} from '../../constants/default-tag-group-definitions.constants';
import {VaultQuery} from '../../queries/vault.query';
import {AuthService} from '../auth/auth.service';
import {ApiErrorCodeEnum} from '../../enums/api-error-code.enum';
import {VaultService as BfaVaultService} from '../../bfa-api/services/vault.service';
import {ClickFinderDocumentList} from '../../bfa-api/models/click-finder-document-list';
import {ClickFinderNodeType} from '../../bfa-api/models/click-finder-node-type';
import {AUTH_TOKEN_HTTP_CONTEXT} from '../../constants/auth-token-http-context.constant';
import {API_CONFIGS} from '../../../configs/api.config';

@Injectable({
    providedIn: 'root'
})
export class ClickFinderService {
    private readonly clickFinderStore = inject(ClickFinderStore);
    private readonly apiVaultService = inject(ApiVaultService);
    private readonly apiDocumentTypeCategoriesService = inject(ApiDocumentTypeCategoriesService);
    private readonly apiDocumentTypesService = inject(ApiDocumentTypesService);
    private readonly bfaVaultService = inject(BfaVaultService);
    private readonly authService = inject(AuthService);
    private readonly tagQuery = inject(TagQuery);
    private readonly snackbarService = inject(SnackbarService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly ICONS = ICONS;

    public reset(): void {
        this.resetNodes();
        this.resetParentNodes();
    }

    public async fetchVaultRootNodesIfNotExists(vaultId: string, overwrite: boolean = false): Promise<void> {
        try {
            const vaultRootNodes = structuredClone(this.clickFinderStore.getValue().vaultRootNodes);
            if (!(vaultId in vaultRootNodes) || overwrite) {
                vaultRootNodes[vaultId] = await this.getNodes(vaultId, [], true);
                this.clickFinderStore.update({
                    vaultRootNodes
                });
            }
        } catch (e) {
            console.error(e);
            this.snackbarService.showError('ERROR.CLICK_FINDER_NODE_LOADING');
        }
    }

    public async fetchNodes(vaultId: string, parentNodes: Array<ClickFinderNodeWithIcon>, smartFilter: boolean): Promise<void> {
        try {
            const nodes: Array<ClickFinderNodeWithIcon> = await this.getNodes(vaultId, parentNodes, smartFilter);
            this.clickFinderStore.update({
                nodes
            });
        } catch (e) {
            console.error(e);
            this.snackbarService.showError('ERROR.CLICK_FINDER_NODE_LOADING');
        }
    }

    public setActiveRootNode(activeRootNode: ClickFinderNodeWithIcon | undefined): void {
        this.clickFinderStore.update({
            activeRootNode,
        });
        this.setActiveNode(activeRootNode);
    }

    public addParentNode(node: ClickFinderNodeWithIcon): void {
        const parentNodes = structuredClone(this.clickFinderStore.getValue().parentNodes);
        parentNodes.push(node);
        this.clickFinderStore.update({
            parentNodes
        });
    }

    public async fetchClickFinderDocumentList(
        vaultId: string,
        allNodes: Array<ClickFinderNodeWithIcon>,
    ): Promise<ClickFinderDocumentList> {
        return await lastValueFrom(this.bfaVaultService.vaultGetClickFinderDocumentList({
            vaultId,
            body: {
                count: API_CONFIGS.maxDocumentFetchCount,
                parentNodes: allNodes.map(node => ({
                    clickFinderNodeId: node.nodeId,
                    clickFinderNodeCaption: node.caption,
                    clickFinderNodeType: node.nodeType as unknown as ClickFinderNodeType,
                })),
            },
        }, new HttpContext().set(AUTH_TOKEN_HTTP_CONTEXT, await this.authService.getToken())));
    }

    public removeParentNode(): boolean {
        const parentNodes: Array<ClickFinderNodeWithIcon> = structuredClone(this.clickFinderStore.getValue().parentNodes);
        const removedNode: ClickFinderNodeWithIcon | undefined = parentNodes.pop();
        this.clickFinderStore.update({
            parentNodes,
            activeNode: parentNodes.length > 0 ? parentNodes.at(-1) : undefined
        });
        return removedNode !== undefined;
    }

    public async createDocumentTypeCategory(vaultId: string, categoryName: string): Promise<boolean> {
        try {
            await firstValueFrom(this.apiVaultService.VaultsCreateDocumentTypeCategory({
                Authorization: await this.authService.getBearer(),
                vaultId,
                categoryCreationData: {
                    name: categoryName,
                }
            }));
        } catch (error) {
            const httpError = error as HttpErrorResponse;
            if (httpError?.error?.code) {
                if (httpError.error.code === ApiErrorCodeEnum.NameExists) {
                    this.snackbarService.showError('ERROR.DOCUMENT_TYPE_CATEGORY.CODE_1003', error as Error);
                } else {
                    this.snackbarService.showError('ERROR.CODES.CODE_' + httpError.error.code as TranslationKey, error as Error);
                }
            } else {
                this.snackbarService.showError('ERROR.ADD_DOCUMENT_TYPE_CATEGORY', error as Error);
            }

            return false;
        }

        return true;
    }

    public async createDocumentType(documentTypeCategoryId: string, documentTypeName: string): Promise<DocumentType> {
        return await firstValueFrom(this.apiDocumentTypeCategoriesService.DocumentTypeCategoriesCreateDocumentType({
            Authorization: await this.authService.getBearer(),
            documentTypeCategoryId,
            creationData: {
                name: documentTypeName,
            }
        }));
    }

    public async assignDocumentTypeTagGroupDefinitions(documentTypeId: string, tagGroupDefinitionIds: Array<string>): Promise<void> {
        await firstValueFrom(this.apiDocumentTypesService.DocumentTypesAssignTagGroupDefinitions({
            Authorization: await this.authService.getBearer(),
            documentTypeId,
            data: {
                orderedTagGroupDefinitionIds: tagGroupDefinitionIds
            }
        }));
    }

    public toggleUsingSmartFilter(): void {
        const isUsingSmartFilter = !this.clickFinderStore.getValue().isUsingSmartFilter;
        this.clickFinderStore.update({ isUsingSmartFilter });
    }

    public setActiveNode(node: ClickFinderNodeWithIcon | undefined): void {
        this.clickFinderStore.update({ activeNode: node });
    }

    public async getRootNodeByVaultId(vaultId: string, isDocumentTypeRootNode: boolean): Promise<ClickFinderNodeWithIcon | undefined> {
        await this.fetchVaultRootNodesIfNotExists(vaultId);
        let vaultRootNodes = this.clickFinderStore.getValue().vaultRootNodes[vaultId];
        if (vaultRootNodes.length === 0) {
            await this.fetchVaultRootNodesIfNotExists(vaultId, true);
            vaultRootNodes = this.clickFinderStore.getValue().vaultRootNodes[vaultId];
        }

        if (isDocumentTypeRootNode) {
            const rootDocumentTypeNode = vaultRootNodes.findLast(rootNode => rootNode.nodeType === 'RootDocumentTypeNode');

            if (rootDocumentTypeNode) {
                return rootDocumentTypeNode;
            }
        }

        return vaultRootNodes.findLast(rootNode => rootNode.nodeType === 'RootTagNode');
    }

    private resetNodes(): void {
        this.clickFinderStore.update({
            nodes: []
        });
    }

    private resetParentNodes(): void {
        this.clickFinderStore.update({
            parentNodes: []
        });
    }

    private async getNodes(vaultId: string, originalParentNodes: Array<ClickFinderNodeWithIcon>, smartFilter: boolean): Promise<Array<ClickFinderNodeWithIcon>> {
        let nodes: Array<ClickFinderNodeWithIcon> = [];
        try {
            const parentNodes: Array<ClickFinderNode> = this.removeIconsFromNodes(originalParentNodes);
            nodes = await firstValueFrom(this.apiVaultService.VaultsCreateClickFinderNodes({
                    vaultId,
                    Authorization: await this.authService.getBearer(),
                    data: {
                        parentNodes,
                        smartFilter
                    }
                })
                .pipe(map(n => this.addIconsToNodes(n))));
        } catch (e) {
            this.snackbarService.showError('ERROR.CLICK_FINDER_NODE_LOADING', e as Error);
        }
        return nodes;
    }

    private getNodeIcon(node: ClickFinderNode): Icon {
        let icon: Icon;
        switch (node.nodeType) {
            case 'TagDefinition': {
                icon = ICONS.CUBE_GREY;

                const documentTagDefinitions = this.tagQuery.getDocumentVaultTagDefinitions();
                if (documentTagDefinitions) {
                    const documentTagDefinition = documentTagDefinitions.find(definition => definition.data.id === node.nodeId);
                    if (documentTagDefinition) {
                        icon = documentTagDefinition.image;
                    }
                }
                break;
            }
            case 'StringEquals':
            case 'Year':
            case 'Month':
            case 'Day':
            case 'Today':
            case 'Yesterday':
            case 'LastWeek':
            case 'NextWeek':
            case 'SelectionValue':
                icon = this.ICONS.TREEITEM;
                break;
            case 'RootTagNode':
                icon = this.ICONS.PROPERTIES;
                break;
            case 'TagGroupDefinition': {
                icon = this.ICONS.PROPGROUP_GREY;

                const defaultTagGroupDefinition = defaultTagGroupDefinitions.find(tagGroupDefinition => tagGroupDefinition.tagGroupDefinitionId === node.nodeId);
                if (defaultTagGroupDefinition) {
                    icon = defaultTagGroupDefinition.icon;
                    break;
                }

                const activeVaultId = this.vaultQuery.getFirstActiveVaultId();
                if (!activeVaultId) {
                    break;
                }

                const userDefinedTagGroupDefinitions = this.vaultQuery.getTagGroupDefinitions(activeVaultId);
                const userDefinedTagGroupDefinition = userDefinedTagGroupDefinitions?.find(tagGroupDefinition => tagGroupDefinition.id === node.nodeId);
                if (userDefinedTagGroupDefinition) {
                    icon = userDefinedTagGroupDefinition.multi ? ICONS.MULTIPROPERTYGROUP_GREEN : ICONS.PROPGROUP_GREEN;
                }
                break;
            }
            case 'NumberRange':
            case 'StringStarts':
            case 'DocumentTypeCategory':
                icon = this.ICONS.SEPERATOR;
                break;
            case 'RootDocumentTypeNode':
            case 'DocumentType':
            default:
                icon = this.ICONS.DOCTYPE;
        }
        return icon;
    }

    private addIconsToNodes(originalNodes: Array<ClickFinderNode>): Array<ClickFinderNodeWithIcon> {
        const nodesWithIcons: Array<ClickFinderNodeWithIcon> = [];
        for (const node of originalNodes) {
            const icon = this.getNodeIcon(node);
            nodesWithIcons.push({
                nodeId: node.nodeId,
                nodeType: node.nodeType,
                caption: node.caption,
                icon
            });
        }
        return nodesWithIcons;
    }

    private removeIconsFromNodes(originalNodes: Array<ClickFinderNodeWithIcon>): Array<ClickFinderNode> {
        const nodes: Array<ClickFinderNode> = [];
        for (const node of originalNodes) {
            nodes.push({
                nodeId: node.nodeId,
                nodeType: node.nodeType,
                caption: node.caption,
            });
        }
        return nodes;
    }
}
